import { debounce } from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { ChangeEvent, KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';

type UseInputSearchParams = {
  debounceMs: number;
  onChange?: (value: string) => void;
  recentSearches: string[];
  value?: string;
};

/**
 * Logic for the InputSearch atom.
 *
 * - Manages the controlled internal value (synced when the consumer resets
 *   `value` to empty) and emits the query via a debounced `onChange`.
 * - Controls the recent-searches dropdown: it opens on focus/click (only when
 *   there are recent searches) and closes on Enter, on focus-out and click-outside
 *   (document listeners) and when a recent search is repeated or cleared.
 * - Exposes `t` so the component can resolve its default labels/texts (the
 *   `useTranslation` hook lives here, not in the component).
 */
function useInputSearch({ debounceMs, onChange, recentSearches, value }: UseInputSearchParams) {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const [inputVal, setInputVal] = useState<string>(value ?? '');
  const [showRecentSearches, setShowRecentSearches] = useState<boolean>(false);

  const debouncedOnChange = useMemo(
    () => debounce((query: string) => onChange?.(query), debounceMs),
    [debounceMs, onChange]
  );

  // Keep the internal value in sync when the consumer resets `value` to empty.
  useEffect(() => {
    if (value === '') {
      setInputVal('');
    }
  }, [value]);

  // Close the dropdown when interacting/focusing outside the component. `mousedown`
  // covers clicks on non-focusable areas; `focusout` covers keyboard Tab-out (checking
  // the element gaining focus via `relatedTarget`). Listening on `document` avoids an
  // `onBlur` handler on the non-interactive container.
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setShowRecentSearches(false);
      }
    }

    function handleFocusOut(event: FocusEvent) {
      if (
        containerRef.current &&
        containerRef.current.contains(event.target as Node) &&
        !containerRef.current.contains(event.relatedTarget as Node | null)
      ) {
        setShowRecentSearches(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('focusout', handleFocusOut);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('focusout', handleFocusOut);
    };
  }, []);

  // Cancel any pending debounced call on unmount.
  useEffect(() => () => debouncedOnChange.cancel(), [debouncedOnChange]);

  function handleChange(event: ChangeEvent<HTMLInputElement>) {
    const nextValue = event.target.value;
    setInputVal(nextValue);
    debouncedOnChange(nextValue);
  }

  function onShowRecentSearches() {
    if (recentSearches.length > 0) {
      setShowRecentSearches(true);
    }
  }

  function onHideRecentSearches() {
    setShowRecentSearches(false);
  }

  function onRepeatSearch(item: string) {
    setInputVal(item);
    setShowRecentSearches(false);
    onChange?.(item);
  }

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Enter') {
      onHideRecentSearches();
    }
  }

  return {
    containerRef,
    handleChange,
    handleKeyDown,
    inputVal,
    onRepeatSearch,
    onShowRecentSearches,
    showRecentSearches,
    t,
  };
}

export default useInputSearch;
